import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, X, Sparkles, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { trackLead } from "@/lib/analytics";
import { SALE, saleActive } from "@/lib/sale";

const seenKey = () => `kc_sale_popup_${new Date().toISOString().slice(0, 10)}`;
const UNLOCK_KEY = "kc_sale_unlocked";

export const SalePopup = () => {
  const [open, setOpen] = useState(false);
  const [unlocked, setUnlocked] = useState(() => localStorage.getItem(UNLOCK_KEY) === "1");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!saleActive()) return;
    if (localStorage.getItem(seenKey()) === "1") return;
    const t = setTimeout(() => setOpen(true), 1800);
    return () => clearTimeout(t);
  }, []);

  const close = () => { localStorage.setItem(seenKey(), "1"); setOpen(false); };

  const submit = async (e) => {
    e.preventDefault();
    const val = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) { toast.error("Please enter a valid email."); return; }
    setSubmitting(true);
    try {
      await api.post("/leads", { email: val, source: "sale_popup" });
      trackLead("sale_popup");
      localStorage.setItem(UNLOCK_KEY, "1");
      setUnlocked(true);
      toast.success("You're in! Your free shipping code is ready.");
    } catch {
      // Still unlock so we never block a shopper over a save hiccup
      localStorage.setItem(UNLOCK_KEY, "1");
      setUnlocked(true);
    } finally { setSubmitting(false); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(SALE.code);
      setCopied(true);
      toast.success(`Code ${SALE.code} copied`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — code is " + SALE.code);
    }
  };

  if (!open || !saleActive()) return null;
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="sale-popup">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-[fadeIn_0.3s_ease]" onClick={close} />
      <div className="relative w-full max-w-md bg-[#3A0A1E] text-white border border-[#F0B429]/30 shadow-2xl overflow-hidden animate-[popIn_0.35s_cubic-bezier(0.16,1,0.3,1)]">
        <button onClick={close} data-testid="sale-popup-close" aria-label="Close"
          className="absolute right-3 top-3 z-10 p-2 text-white/60 hover:text-white transition-colors"><X size={18} /></button>

        <div className="px-8 pt-10 pb-8 text-center">
          <div className="inline-flex items-center gap-2 text-[#F0B429] mb-3">
            <Sparkles size={16} strokeWidth={1.8} />
            <span className="text-[0.7rem] font-mono tracking-[0.22em] uppercase">Special Offer</span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl leading-none tracking-tight uppercase">{SALE.headline}</h2>
          <p className="font-display text-2xl mt-1 text-white/90 capitalize">{SALE.subline}</p>

          {!unlocked ? (
            <>
              <p className="mt-3 text-sm text-white/70 font-body">Enter your email to unlock your code — plus first dibs on new arrivals &amp; future sales.</p>
              <form onSubmit={submit} className="mt-5 flex flex-col gap-2.5" data-testid="sale-email-form">
                <div className="flex items-center gap-2 bg-white/10 border border-white/25 rounded-md px-3">
                  <Mail size={16} className="text-white/60 flex-shrink-0" />
                  <input
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                    placeholder="you@email.com" data-testid="sale-email-input"
                    className="w-full bg-transparent py-3 text-sm text-white placeholder-white/40 focus:outline-none"
                  />
                </div>
                <button type="submit" disabled={submitting} data-testid="sale-email-submit"
                  className="w-full bg-[#F0B429] text-[#3A0A1E] py-3.5 text-[0.72rem] font-mono tracking-[0.2em] uppercase font-semibold hover:bg-white transition-colors disabled:opacity-60">
                  {submitting ? "Unlocking…" : "Unlock Free Shipping"}
                </button>
              </form>
              <button onClick={close} className="mt-3 text-[0.72rem] text-white/50 hover:text-white/80 font-body underline underline-offset-2">
                No thanks, maybe later
              </button>
            </>
          ) : (
            <>
              <p className="mt-3 text-sm text-white/70 font-body">Your code is ready — applies automatically at checkout.</p>
              <div className="mt-4 flex items-center justify-center">
                <div className="flex items-stretch border border-dashed border-[#F0B429]/60 rounded-md overflow-hidden">
                  <span className="px-4 py-2.5 font-mono text-lg tracking-[0.2em] text-white">{SALE.code}</span>
                  <button onClick={copy} data-testid="sale-popup-copy"
                    className="px-3 flex items-center gap-1.5 bg-[#F0B429] text-[#3A0A1E] text-xs font-mono uppercase tracking-wider font-semibold hover:bg-white transition-colors">
                    {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
                  </button>
                </div>
              </div>
              <Link to={SALE.cta} onClick={close} data-testid="sale-popup-shop"
                className="mt-6 block w-full bg-white text-[#3A0A1E] py-3.5 text-[0.72rem] font-mono tracking-[0.2em] uppercase font-semibold hover:bg-[#F0B429] transition-colors">
                Shop the Sale
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
